/**
 * PortfolioOptimizerPage
 * ─────────────────────
 * Lets users pick N saved backtest strategies, name a "combo", choose an
 * optimizer method (equal / risk_parity / max_sharpe / min_vol / monte_carlo),
 * and see the combined equity curve, metrics, weight pie and member contributions.
 *
 * Props:
 *   analyticsData   – lifted from OverviewPage: { summary, cumulative }
 *                     analyticsData.summary.metadata is keyed by strategy label,
 *                     each entry has { backtest_id, sharpe, total_return, ... }
 */
import React, { useState, useEffect, useCallback } from 'react';
import PlotlyChart from '../../../components/charts/PlotlyChart';
import { tradingService } from '../../../services/tradingService';

// ─── helpers ────────────────────────────────────────────────────────────────

const fmt = (v, digits = 2) =>
  v == null ? '—' : (v * 100).toFixed(digits) + '%';
const fmtNum = (v, digits = 2) =>
  v == null ? '—' : Number(v).toFixed(digits);

const methodMeta = {
  equal:       { label: 'Equal',        desc: '1/N weight for every strategy' },
  risk_parity: { label: 'Risk Parity',  desc: 'Inverse-volatility, normalized' },
  max_sharpe:  { label: 'Max Sharpe',   desc: 'scipy SLSQP, long-only + sum=1' },
  min_vol:     { label: 'Min Vol',      desc: 'Minimize portfolio variance' },
  monte_carlo: { label: 'Monte Carlo',  desc: '10 000 random portfolios, max Sharpe' },
};

const pillCls = (active) =>
  `px-3 py-1.5 rounded-lg text-xs font-medium transition-colors cursor-pointer ${
    active
      ? 'bg-brand-gold text-white'
      : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
  }`;

const GOLD = '#c9a96e';
const GOLD_FAINT = 'rgba(201,169,110,0.18)';

// ─── MetricTile ─────────────────────────────────────────────────────────────

const MetricTile = ({ label, value, color }) => (
  <div className="bg-gray-50 dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl px-4 py-3 text-center min-w-0">
    <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">{label}</div>
    <div className={`text-base font-bold font-mono ${color || 'text-gray-800 dark:text-gray-100'}`}>
      {value}
    </div>
  </div>
);

// ─── WeightsPie ─────────────────────────────────────────────────────────────

const WeightsPie = ({ members }) => {
  const data = [{
    type: 'pie',
    hole: 0.45,
    labels: members.map(m => m.label),
    values: members.map(m => +(m.weight * 100).toFixed(2)),
    textinfo: 'label+percent',
    hovertemplate: '<b>%{label}</b><br>Weight: %{value:.1f}%<extra></extra>',
    marker: {
      colors: [
        GOLD, '#4e9af1', '#6bcb77', '#ff6b6b', '#f7b731',
        '#a29bfe', '#fd79a8', '#74b9ff', '#55efc4', '#fdcb6e',
      ],
    },
  }];
  return (
    <PlotlyChart
      data={data}
      layout={{
        showlegend: false,
        margin: { l: 10, r: 10, t: 10, b: 10 },
      }}
      style={{ minHeight: 260 }}
    />
  );
};

// ─── EquityCurveChart ────────────────────────────────────────────────────────

const EquityCurveChart = ({ combo, analyticsData }) => {
  const curve = combo.equity_curve || [];
  const dates  = curve.map(p => p.date);
  const equity = curve.map(p => p.equity);

  const traces = [
    {
      x: dates, y: equity,
      name: combo.name,
      type: 'scatter', mode: 'lines',
      line: { color: GOLD, width: 2.5 },
    },
  ];

  // Overlay member monthly cumulative curves from analyticsData (rebased to 10000)
  const cumRaw = analyticsData?.cumulative;
  const cumData = cumRaw?.cumulative || cumRaw;
  if (cumData?.index && cumData?.columns) {
    combo.members.forEach((m, idx) => {
      const colIdx = cumData.columns.indexOf(m.label);
      if (colIdx === -1) return;
      const yVals = cumData.data.map(row => {
        const v = row[colIdx];
        return v == null ? null : (1 + v) * 10000;
      });
      const MEMBER_COLORS = [
        '#4e9af1','#6bcb77','#ff6b6b','#f7b731',
        '#a29bfe','#fd79a8','#74b9ff','#55efc4','#fdcb6e',
      ];
      traces.push({
        x: cumData.index, y: yVals,
        name: m.label,
        type: 'scatter', mode: 'lines',
        line: { color: MEMBER_COLORS[idx % MEMBER_COLORS.length], width: 1, dash: 'dot' },
        opacity: 0.55,
      });
    });
  }

  return (
    <PlotlyChart
      data={traces}
      layout={{
        yaxis: { title: 'Portfolio Value ($)', tickformat: ',.0f' },
        xaxis: { title: '' },
        legend: { orientation: 'h', yanchor: 'top', y: -0.15, xanchor: 'center', x: 0.5, font: { size: 9 } },
      }}
      style={{ minHeight: 300 }}
    />
  );
};

// ─── FrontierScatter ─────────────────────────────────────────────────────────

const FrontierScatter = ({ frontier, bestWeights, members }) => {
  if (!frontier || frontier.length === 0) return null;

  const vols    = frontier.map(p => +(p.vol * 100).toFixed(3));
  const rets    = frontier.map(p => +(p.ret * 100).toFixed(3));
  const sharpes = frontier.map(p => p.sharpe);

  // Best point
  const bestVol = frontier.reduce((prev, cur) =>
    Math.abs(cur.sharpe - Math.max(...sharpes)) < Math.abs(prev.sharpe - Math.max(...sharpes)) ? cur : prev
  );

  return (
    <PlotlyChart
      data={[
        {
          x: vols, y: rets,
          mode: 'markers',
          type: 'scatter',
          marker: {
            size: 5,
            color: sharpes,
            colorscale: 'Viridis',
            showscale: true,
            colorbar: { title: 'Sharpe', thickness: 12, len: 0.8 },
          },
          hovertemplate: 'Vol: %{x:.1f}%<br>Ret: %{y:.1f}%<br>Sharpe: %{marker.color:.2f}<extra></extra>',
          name: 'Simulated',
        },
        {
          x: [+(bestVol.vol * 100).toFixed(3)],
          y: [+(bestVol.ret * 100).toFixed(3)],
          mode: 'markers',
          type: 'scatter',
          marker: { size: 14, color: GOLD, symbol: 'star', line: { color: '#fff', width: 1 } },
          name: 'Optimal',
          hovertemplate: 'OPTIMAL<br>Vol: %{x:.1f}%<br>Ret: %{y:.1f}%<extra></extra>',
        },
      ]}
      layout={{
        xaxis: { title: 'Ann. Volatility (%)', tickformat: '.1f' },
        yaxis: { title: 'Ann. Return (%)',     tickformat: '.1f' },
        showlegend: true,
      }}
      interactive
      style={{ minHeight: 320 }}
    />
  );
};

// ─── MembersTable ─────────────────────────────────────────────────────────────

const MembersTable = ({ members }) => (
  <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800">
    <table className="w-full text-xs">
      <thead>
        <tr className="border-b border-gray-200 dark:border-gray-800">
          {['Strategy','Weight','Contrib. Return','Contrib. Vol'].map(h => (
            <th key={h} className="px-3 py-2 text-left text-[10px] uppercase tracking-wider text-gray-400 font-medium">
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {[...members].sort((a,b) => b.weight - a.weight).map((m, i) => (
          <tr key={i} className="border-b border-gray-100 dark:border-gray-800/50 hover:bg-gray-50 dark:hover:bg-white/3">
            <td className="px-3 py-2 text-gray-800 dark:text-gray-200 font-medium max-w-[180px] truncate">{m.label}</td>
            <td className="px-3 py-2 font-mono text-brand-gold font-bold">{fmt(m.weight)}</td>
            <td className={`px-3 py-2 font-mono ${
              (m.contribution?.contribution_return ?? 0) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-500'
            }`}>{fmt(m.contribution?.contribution_return)}</td>
            <td className="px-3 py-2 font-mono text-gray-600 dark:text-gray-400">
              {fmt(m.contribution?.contribution_vol)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// ─── ResultsModal ────────────────────────────────────────────────────────────

const BASKET_METHODS = ['equal', 'risk_parity', 'max_sharpe'];

const ResultsModal = ({ combo, onClose, analyticsData }) => {
  const [rerunMethod, setRerunMethod] = useState(combo.optimizer_method);
  const [rerunning, setRerunning]     = useState(false);
  const [current, setCurrent]         = useState(combo);
  const [rerunErr, setRerunErr]       = useState('');

  // ── Deploy state ──────────────────────────────────────────────────────────
  const [basketName,     setBasketName]     = useState(combo.name);
  const [totalCapital,   setTotalCapital]   = useState('50000');
  const [deploying,      setDeploying]      = useState(false);
  const [deployErr,      setDeployErr]      = useState('');
  const [deploySuccess,  setDeploySuccess]  = useState(false);

  const m = current.portfolio_metrics || {};
  const colorSign = (v) =>
    v == null ? 'text-gray-400' : v >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-500';

  const handleRerun = async () => {
    setRerunning(true);
    setRerunErr('');
    try {
      const res = await tradingService.rerunPortfolioCombo(current.id, { optimizer_method: rerunMethod });
      setCurrent(res.data);
    } catch (e) {
      setRerunErr(e.response?.data?.error || 'Rerun failed.');
    } finally {
      setRerunning(false);
    }
  };

  const handleDeploy = async () => {
    if (!basketName.trim()) { setDeployErr('Enter a basket name.'); return; }
    const cap = parseFloat(totalCapital);
    if (isNaN(cap) || cap <= 0) { setDeployErr('Enter a valid total capital amount.'); return; }

    // Check all members have strategy_class + symbol (requires backend fix deployed)
    const missingFields = (current.members || []).filter(m => !m.strategy_class || !m.symbol);
    if (missingFields.length > 0) {
      setDeployErr('Some strategies are missing deploy metadata. Try re-opening this combo.');
      return;
    }

    setDeploying(true);
    setDeployErr('');
    try {
      // StrategyPortfolio only stores equal/risk_parity/max_sharpe — map others to 'equal'
      const basketMethod = BASKET_METHODS.includes(current.optimizer_method)
        ? current.optimizer_method : 'equal';

      await tradingService.deployBasket({
        name:             basketName.trim(),
        total_capital:    cap,
        optimizer_method: basketMethod,
        members: (current.members || []).map(m => ({
          label:          m.label,
          weight:         m.weight,
          allocation_usd: +(m.weight * cap).toFixed(2),
          strategy_class: m.strategy_class,
          symbol:         m.symbol,
          parameters:     m.parameters || {},
        })),
      });
      setDeploySuccess(true);
    } catch (e) {
      setDeployErr(e.response?.data?.error || 'Deploy failed.');
    } finally {
      setDeploying(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div className="w-full max-w-4xl max-h-[93vh] bg-white dark:bg-[#0d1117] rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-800 shrink-0">
          <div>
            <h2 className="font-serif text-lg text-gray-900 dark:text-gray-100">{current.name}</h2>
            <span className="text-[10px] uppercase tracking-wider text-gray-400">
              {methodMeta[current.optimizer_method]?.label} · {current.members?.length ?? 0} strategies
            </span>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-xl leading-none shrink-0">×</button>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-6">

          {/* Metrics row */}
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
            <MetricTile label="Sharpe"    value={fmtNum(m.sharpe)}      color={colorSign(m.sharpe)} />
            <MetricTile label="CAGR"      value={fmt(m.cagr)}           color={colorSign(m.cagr)} />
            <MetricTile label="Max DD"    value={fmt(m.max_drawdown)}   color="text-red-500" />
            <MetricTile label="Vol"       value={fmt(m.volatility)}     />
            <MetricTile label="Calmar"    value={fmtNum(m.calmar)}      color={colorSign(m.calmar)} />
            <MetricTile label="Total Ret" value={fmt(m.total_return)}   color={colorSign(m.total_return)} />
          </div>

          {/* Weights pie + equity curve side by side on desktop */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-2">Weight Allocation</p>
              <WeightsPie members={current.members || []} />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-2">Combined Equity Curve</p>
              <EquityCurveChart combo={current} analyticsData={analyticsData} />
            </div>
          </div>

          {/* Members table */}
          <div>
            <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-2">Member Contributions</p>
            <MembersTable members={current.members || []} />
          </div>

          {/* Efficient frontier (monte_carlo only) */}
          {current.optimizer_method === 'monte_carlo' && current.efficient_frontier?.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-2">
                Efficient Frontier — {current.efficient_frontier.length} simulated portfolios
              </p>
              <FrontierScatter
                frontier={current.efficient_frontier}
                bestWeights={current.weights}
                members={current.members || []}
              />
            </div>
          )}

          {/* Re-run with different method */}
          <div className="border-t border-gray-200 dark:border-gray-800 pt-4">
            <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-3">Re-run with Different Method</p>
            <div className="flex flex-wrap gap-2 mb-3">
              {Object.entries(methodMeta).map(([k, v]) => (
                <button key={k} className={pillCls(rerunMethod === k)} onClick={() => setRerunMethod(k)}>
                  {v.label}
                </button>
              ))}
            </div>
            {rerunErr && <p className="text-xs text-red-500 mb-2">{rerunErr}</p>}
            <button
              disabled={rerunning || rerunMethod === current.optimizer_method}
              onClick={handleRerun}
              className="px-4 py-2 rounded-lg bg-brand-gold text-white text-xs font-semibold uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed hover:brightness-110 transition-all"
            >
              {rerunning ? 'Running…' : 'Apply'}
            </button>
          </div>

          {/* ── Deploy as Live Basket ─────────────────────────────────── */}
          <div className="border-t border-gray-200 dark:border-gray-800 pt-4">
            <div className="flex items-center gap-2 mb-3">
              <svg className="w-4 h-4 text-brand-gold shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
              </svg>
              <p className="text-[10px] uppercase tracking-wider text-gray-400">Deploy as Live Basket</p>
            </div>

            {deploySuccess ? (
              <div className="flex items-center gap-3 px-4 py-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/40 rounded-xl">
                <svg className="w-5 h-5 text-green-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <div className="flex-1">
                  <p className="text-sm font-medium text-green-700 dark:text-green-400">Basket deployed!</p>
                  <p className="text-xs text-green-600 dark:text-green-500 mt-0.5">
                    Find it in the <strong>Baskets</strong> tab to activate and monitor live signals.
                  </p>
                </div>
              </div>
            ) : (
              <>
                <p className="text-xs text-gray-400 mb-3">
                  Creates a live strategy basket using the optimized weights above.
                  {!BASKET_METHODS.includes(current.optimizer_method) && (
                    <span className="text-yellow-500 dark:text-yellow-400">
                      {' '}The basket label will show "Equal" (weights from {methodMeta[current.optimizer_method]?.label} are preserved).
                    </span>
                  )}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="block text-[10px] uppercase tracking-wider text-gray-400 mb-1">Basket Name</label>
                    <input
                      type="text"
                      value={basketName}
                      onChange={e => setBasketName(e.target.value)}
                      className="w-full bg-gray-50 dark:bg-[#161b22] border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-800 dark:text-gray-200 outline-none focus:ring-2 focus:ring-brand-gold/30"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-wider text-gray-400 mb-1">Total Capital ($)</label>
                    <input
                      type="number"
                      min="1000"
                      step="1000"
                      value={totalCapital}
                      onChange={e => setTotalCapital(e.target.value)}
                      className="w-full bg-gray-50 dark:bg-[#161b22] border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-800 dark:text-gray-200 outline-none focus:ring-2 focus:ring-brand-gold/30"
                    />
                  </div>
                </div>

                {/* Allocation preview */}
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {(current.members || []).map(m => (
                    <span key={m.label} className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500">
                      {m.label.split(' on ').pop() || m.label}
                      {' · '}
                      <span className="text-brand-gold font-medium">
                        ${Math.round(m.weight * (parseFloat(totalCapital) || 0)).toLocaleString()}
                      </span>
                    </span>
                  ))}
                </div>

                {deployErr && (
                  <p className="text-xs text-red-500 mb-2">{deployErr}</p>
                )}

                <button
                  onClick={handleDeploy}
                  disabled={deploying}
                  className="px-5 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white text-xs font-semibold uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                >
                  {deploying ? 'Deploying…' : '🚀 Deploy to Baskets'}
                </button>
              </>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

// ─── ComboCard ───────────────────────────────────────────────────────────────

const ComboCard = ({ combo, onView, onDelete }) => {
  const [deleting, setDeleting] = useState(false);
  const m = combo.portfolio_metrics || {};
  const topMembers = (combo.members_summary || []).slice(0, 2);
  const extra = (combo.member_count || 0) - topMembers.length;

  const handleDelete = async (e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${combo.name}"?`)) return;
    setDeleting(true);
    try { await onDelete(combo.id); }
    finally { setDeleting(false); }
  };

  return (
    <div className="bg-white dark:bg-gray-900/50 border border-gray-200 dark:border-gray-800 rounded-xl p-4 hover:border-brand-gold/50 transition-colors">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0">
          <h3 className="font-medium text-gray-800 dark:text-gray-100 text-sm truncate">{combo.name}</h3>
          <span className="inline-block mt-0.5 text-[10px] px-2 py-0.5 rounded-full bg-brand-gold/15 text-brand-gold font-medium">
            {methodMeta[combo.optimizer_method]?.label || combo.optimizer_method}
          </span>
        </div>
        <button
          onClick={handleDelete}
          disabled={deleting}
          className="shrink-0 text-gray-400 hover:text-red-400 transition-colors text-sm disabled:opacity-40"
          title="Delete"
        >
          {deleting ? '…' : '✕'}
        </button>
      </div>

      <div className="grid grid-cols-3 gap-2 mb-3">
        <div className="text-center">
          <div className="text-[10px] text-gray-400">Sharpe</div>
          <div className={`text-sm font-bold font-mono ${m.sharpe >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-500'}`}>
            {m.sharpe != null ? fmtNum(m.sharpe) : '—'}
          </div>
        </div>
        <div className="text-center">
          <div className="text-[10px] text-gray-400">CAGR</div>
          <div className={`text-sm font-bold font-mono ${m.cagr >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-500'}`}>
            {m.cagr != null ? fmt(m.cagr) : '—'}
          </div>
        </div>
        <div className="text-center">
          <div className="text-[10px] text-gray-400">Max DD</div>
          <div className="text-sm font-bold font-mono text-red-500">
            {m.max_drawdown != null ? fmt(m.max_drawdown) : '—'}
          </div>
        </div>
      </div>

      {topMembers.length > 0 && (
        <p className="text-[10px] text-gray-400 truncate mb-3">
          {topMembers.map(t => `${t.label} (${fmt(t.weight)})`).join(', ')}
          {extra > 0 && ` +${extra} more`}
        </p>
      )}

      <button
        onClick={() => onView(combo.id)}
        className="w-full py-1.5 rounded-lg text-xs font-medium border border-brand-gold/40 text-brand-gold hover:bg-brand-gold hover:text-white transition-colors"
      >
        View Results
      </button>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const PortfolioOptimizerPage = ({ analyticsData }) => {
  // ── Strategies available in the current analytics view ───────────────────
  const strategies = Object.entries(analyticsData?.summary?.metadata || {}).map(([label, meta]) => ({
    label,
    backtest_id: meta.backtest_id,
    sharpe:      meta.sharpe,
    total_return: meta.total_return,
  }));

  // ── Builder state ─────────────────────────────────────────────────────────
  const [selected, setSelected]     = useState(new Set());
  const [comboName, setComboName]   = useState('');
  const [method, setMethod]         = useState('max_sharpe');
  const [building, setBuilding]     = useState(false);
  const [buildErr, setBuildErr]     = useState('');

  const toggleStrategy = (label) =>
    setSelected(prev => {
      const next = new Set(prev);
      next.has(label) ? next.delete(label) : next.add(label);
      return next;
    });

  // ── Combos list ───────────────────────────────────────────────────────────
  const [combos, setCombos]         = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listErr, setListErr]       = useState('');

  const [viewCombo, setViewCombo]   = useState(null);   // full detail for modal
  const [modalLoading, setModalLoading] = useState(false);

  const loadCombos = useCallback(async () => {
    setListLoading(true);
    setListErr('');
    try {
      const res = await tradingService.listPortfolioCombos();
      setCombos(res.data?.combos || []);
    } catch {
      setListErr('Failed to load saved combos.');
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => { loadCombos(); }, [loadCombos]);

  // ── Build ─────────────────────────────────────────────────────────────────
  const handleBuild = async () => {
    if (selected.size < 2) { setBuildErr('Select at least 2 strategies.'); return; }
    if (!comboName.trim()) { setBuildErr('Enter a name for this combo.'); return; }
    setBuildErr('');
    setBuilding(true);
    try {
      const members = [...selected].map(label => {
        const s = strategies.find(st => st.label === label);
        return { backtest_result_id: s.backtest_id, label };
      });
      await tradingService.createPortfolioCombo({
        name: comboName.trim(),
        optimizer_method: method,
        members,
      });
      setComboName('');
      setSelected(new Set());
      await loadCombos();
    } catch (e) {
      setBuildErr(e.response?.data?.error || 'Failed to build portfolio.');
    } finally {
      setBuilding(false);
    }
  };

  // ── View detail ───────────────────────────────────────────────────────────
  const handleView = async (id) => {
    setModalLoading(true);
    try {
      const res = await tradingService.getPortfolioCombo(id);
      setViewCombo(res.data);
    } catch {
      // ignore
    } finally {
      setModalLoading(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = async (id) => {
    await tradingService.deletePortfolioCombo(id);
    await loadCombos();
  };

  // ── No strategies yet ─────────────────────────────────────────────────────
  if (strategies.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <div className="text-4xl mb-4">📊</div>
        <h3 className="font-serif text-lg text-gray-700 dark:text-gray-300 mb-2">No strategies in analytics yet</h3>
        <p className="text-sm text-gray-400 max-w-sm">
          Go to Overview — save some backtest results first. Once saved, they'll appear here for portfolio-level weight optimization.
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

      {/* ── LEFT: Builder ─────────────────────────────────────────── */}
      <div className="space-y-4">
        <div className="bg-white dark:bg-gray-900/50 border border-gray-200 dark:border-gray-800 rounded-xl p-5">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Build a Portfolio Combo</h2>
          <p className="text-xs text-gray-400 mb-4">
            Select ≥ 2 strategies, name the combo, pick an optimizer — we find the optimal capital allocation weights.
          </p>

          {/* Name */}
          <div className="mb-4">
            <label className="block text-[10px] uppercase tracking-wider text-gray-400 mb-1">Combo Name</label>
            <input
              type="text"
              value={comboName}
              onChange={e => setComboName(e.target.value)}
              placeholder="e.g. Combo A"
              className="w-full bg-gray-50 dark:bg-[#161b22] border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-800 dark:text-gray-200 outline-none focus:ring-2 focus:ring-brand-gold/30"
            />
          </div>

          {/* Method */}
          <div className="mb-4">
            <label className="block text-[10px] uppercase tracking-wider text-gray-400 mb-2">Optimizer Method</label>
            <div className="flex flex-wrap gap-2">
              {Object.entries(methodMeta).map(([k, v]) => (
                <button key={k} className={pillCls(method === k)} onClick={() => setMethod(k)} title={v.desc}>
                  {v.label}
                </button>
              ))}
            </div>
            <p className="text-[10px] text-gray-400 mt-1.5">{methodMeta[method].desc}</p>
          </div>

          {/* Strategy checklist */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <label className="text-[10px] uppercase tracking-wider text-gray-400">
                Strategies ({selected.size} selected)
              </label>
              {selected.size > 0 && (
                <button
                  onClick={() => setSelected(new Set())}
                  className="text-[10px] text-gray-400 hover:text-red-400 transition-colors"
                >
                  Clear all
                </button>
              )}
            </div>
            <div className="max-h-64 overflow-y-auto space-y-1 border border-gray-200 dark:border-gray-700 rounded-lg p-2">
              {strategies.map(({ label, sharpe, total_return }) => {
                const on = selected.has(label);
                return (
                  <label
                    key={label}
                    className={`flex items-center gap-2.5 px-2 py-1.5 rounded-lg cursor-pointer transition-colors ${
                      on
                        ? 'bg-brand-gold/10 border border-brand-gold/30'
                        : 'hover:bg-gray-50 dark:hover:bg-white/3 border border-transparent'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={() => toggleStrategy(label)}
                      className="accent-brand-gold shrink-0"
                    />
                    <span className="text-xs text-gray-700 dark:text-gray-300 flex-1 min-w-0 truncate">{label}</span>
                    <span className={`text-[10px] font-mono shrink-0 ${total_return >= 0 ? 'text-green-500' : 'text-red-400'}`}>
                      {total_return != null ? fmt(total_return, 1) : ''}
                    </span>
                    {sharpe != null && (
                      <span className="text-[10px] font-mono text-gray-400 shrink-0">
                        SR {fmtNum(sharpe, 1)}
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
          </div>

          {buildErr && (
            <div className="mb-3 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 text-red-600 dark:text-red-400 text-xs">
              {buildErr}
            </div>
          )}

          <button
            onClick={handleBuild}
            disabled={building || selected.size < 2 || !comboName.trim()}
            className="w-full py-2.5 rounded-lg bg-brand-gold text-white text-xs font-semibold uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed hover:brightness-110 transition-all"
          >
            {building ? 'Optimizing…' : `Build Portfolio (${selected.size} strategies)`}
          </button>
        </div>
      </div>

      {/* ── RIGHT: Saved combos ───────────────────────────────────── */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            Saved Combos
            {combos.length > 0 && (
              <span className="ml-2 text-[10px] bg-gray-100 dark:bg-gray-800 text-gray-500 px-1.5 py-0.5 rounded-full">
                {combos.length}
              </span>
            )}
          </h2>
          <button
            onClick={loadCombos}
            className="text-xs text-gray-400 hover:text-brand-gold transition-colors"
          >
            ↺ Refresh
          </button>
        </div>

        {listLoading && (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-brand-gold border-t-transparent" />
          </div>
        )}

        {listErr && (
          <p className="text-xs text-red-400 text-center py-6">{listErr}</p>
        )}

        {!listLoading && !listErr && combos.length === 0 && (
          <div className="text-center py-12 text-gray-400">
            <div className="text-3xl mb-3">📂</div>
            <p className="text-sm">No combos yet. Build your first one.</p>
          </div>
        )}

        {!listLoading && combos.map(combo => (
          <ComboCard
            key={combo.id}
            combo={combo}
            onView={handleView}
            onDelete={handleDelete}
          />
        ))}
      </div>

      {/* ── Modal: full detail ────────────────────────────────────── */}
      {modalLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-brand-gold border-t-transparent" />
        </div>
      )}

      {viewCombo && !modalLoading && (
        <ResultsModal
          combo={viewCombo}
          onClose={() => setViewCombo(null)}
          analyticsData={analyticsData}
        />
      )}
    </div>
  );
};

export default PortfolioOptimizerPage;
